import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Wallet, TrendingUp, DollarSign, Award, ArrowUpRight, ArrowDownRight, Download, Gift, CreditCard } from "lucide-react";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock earnings by service
const earningsByService = [
  { service: "Home Visit", amount: 2800 },
  { service: "Clinic Session", amount: 1500 },
  { service: "Teleconsult", amount: 400 },
  { service: "Assessment", amount: 300 },
];

// Mock monthly trend
const monthlyTrend = [
  { month: "Jul", earnings: 3200 },
  { month: "Aug", earnings: 3500 },
  { month: "Sep", earnings: 3800 },
  { month: "Oct", earnings: 4100 },
  { month: "Nov", earnings: 4500 },
  { month: "Dec", earnings: 5000 },
];

// Mock transactions
const transactions = [
  { id: 1, type: "session", description: "Home Visit - Ahmad Rahman", amount: 180, date: "2024-12-30", status: "completed" },
  { id: 2, type: "bonus", description: "Weekly Performance Bonus", amount: 150, date: "2024-12-29", status: "completed" },
  { id: 3, type: "session", description: "Clinic Session - Sarah Lee", amount: 120, date: "2024-12-28", status: "completed" },
  { id: 4, type: "incentive", description: "Elite Tier Incentive", amount: 200, date: "2024-12-27", status: "completed" },
  { id: 5, type: "session", description: "Teleconsult - Kumar Rajan", amount: 80, date: "2024-12-26", status: "completed" },
];

const transactionIcons = {
  session: DollarSign,
  bonus: Award,
  incentive: Gift,
  withdrawal: CreditCard,
};

const transactionColors = {
  session: "bg-blue-50 text-blue-600",
  bonus: "bg-emerald-50 text-emerald-600",
  incentive: "bg-amber-50 text-amber-600",
  withdrawal: "bg-red-50 text-red-600",
};

export default function TherapistEarnings() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/therapist/stats`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="therapist-earnings">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Earnings</p>
                <p className="font-heading font-bold text-xl text-slate-900">
                  RM {stats?.total_earnings?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">This Month</p>
                <p className="font-heading font-bold text-xl text-slate-900">
                  RM {stats?.monthly_earnings?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Bonuses Earned</p>
                <p className="font-heading font-bold text-xl text-slate-900">RM 850</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Today's Earnings</p>
                <p className="font-heading font-bold text-xl text-slate-900">
                  RM {stats?.today_earnings?.toFixed(0) || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Earnings Trend */}
        <Card className="chart-container">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Earnings Trend</CardTitle>
            <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
              <ArrowUpRight className="w-3 h-3" />
              +15% vs last month
            </Badge>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={monthlyTrend}>
                <defs>
                  <linearGradient id="earningsGrad3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `RM ${v}`} />
                <Tooltip 
                  formatter={(value) => [`RM ${value}`, 'Earnings']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="earnings" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  fill="url(#earningsGrad3)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Earnings by Service */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Earnings by Service Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={earningsByService} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" tickFormatter={(v) => `RM ${v}`} />
                <YAxis type="category" dataKey="service" width={90} tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => [`RM ${value}`, 'Earnings']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="amount" fill="#F40d07" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">Transaction History</CardTitle>
          <Button variant="outline" size="sm" data-testid="download-statement-btn">
            <Download className="w-4 h-4 mr-2" />
            Download Statement
          </Button>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((txn) => {
                const Icon = transactionIcons[txn.type] || DollarSign;
                return (
                  <TableRow key={txn.id}>
                    <TableCell>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${transactionColors[txn.type]}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{txn.description}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-500">{txn.date}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-100 text-emerald-700">
                        {txn.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-bold ${txn.type === 'withdrawal' ? 'text-red-600' : 'text-emerald-600'}`}>
                        {txn.type === 'withdrawal' ? '-' : '+'}RM {txn.amount}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Tier Benefits */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-500" />
            Your Tier Benefits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-emerald-50 rounded-lg text-center">
              <p className="text-2xl font-heading font-bold text-emerald-700">+20%</p>
              <p className="text-sm text-emerald-600">Rate Bonus</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-heading font-bold text-blue-700">Priority</p>
              <p className="text-sm text-blue-600">Session Booking</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <p className="text-2xl font-heading font-bold text-amber-700">Monthly</p>
              <p className="text-sm text-amber-600">Performance Bonus</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Button className="h-16 bg-primary hover:bg-primary-600 text-white" data-testid="request-withdrawal-btn">
          <CreditCard className="w-5 h-5 mr-2" />
          Request Withdrawal
        </Button>
        <Button variant="outline" className="h-16" data-testid="view-incentives-btn">
          <Award className="w-5 h-5 mr-2" />
          View Available Incentives
        </Button>
      </div>
    </div>
  );
}
